'use client';

import { useTranslations } from 'next-intl';
import { BaseLineCoupon } from '@/app/components/icons/BaseLineCoupon';
import { buttonVariants } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/app/components/ui/dialog';
import { SITE } from '@/app/constants';
import { useDoubleFestivalCampaign } from '@/app/hooks';
import { cn } from '@/app/utils';
import { Link } from '@/i18n/routing';
import { Membership } from './Membership';

export const DoubleFestivalDialog = ({
  open,
  onClose,
  onNavigate,
}: {
  open: boolean;
  onClose: () => void;
  onNavigate: () => void;
}) => {
  const t = useTranslations(SITE === 'china' ? 'doubleFestival' : 'doubleFestival.global');
  const visible = useDoubleFestivalCampaign();
  const navigate = () => {
    onNavigate();
    onClose();
  };

  return (
    <Dialog
      open={open && visible}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className="w-[604px] max-w-[calc(100%-32px)] gap-0 rounded-[32px] border-0 bg-[#121316] p-3 ring-1 ring-white/[0.12] ring-inset sm:gap-0 sm:rounded-[24px] md:p-3"
        closeClassName="top-8 right-8 size-6 md:top-8 md:right-8"
      >
        <Link
          href="/subscription/personal"
          onClick={navigate}
          aria-label={t('headline')}
          className="relative block aspect-[16/9] w-full overflow-hidden rounded-[20px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-white"
        >
          <img
            src={
              SITE === 'china'
                ? 'https://image01-aliyun.vidu.zone/vidu/media-asset/china-hero-c5f6c41f.webp'
                : 'https://image01.cf.vidu.studio/vidu/media-asset/global-hero-e260e730.webp'
            }
            width={1160}
            height={653}
            alt=""
            className="absolute inset-0 size-full object-cover"
          />
        </Link>
        <div
          className={cn(
            'flex flex-col items-center gap-5 px-6 pt-4 pb-8 sm:gap-4 sm:px-2 sm:pb-5',
            SITE === 'global' && 'font-Poppins',
          )}
        >
          <DialogTitle className="text-center font-semibold text-[28px] text-white leading-[1.6] sm:text-[22px]">
            {t('title')}
          </DialogTitle>
          <div className="flex w-full items-stretch gap-2 rounded-16 bg-white/5 p-5 ring-1 ring-white/[0.04] ring-inset sm:gap-3 sm:px-3">
            <div className="flex min-h-[97px] min-w-0 flex-1 flex-col items-center justify-center text-center leading-[1.6]">
              <BaseLineCoupon className="mb-1.5 size-6 text-white" />
              <p className="font-semibold text-sm text-white">{t('usageTitle')}</p>
              <p className="text-white/40 text-xs">{t('usageDescription')}</p>
            </div>
            <div className="w-px shrink-0 bg-white/10" aria-hidden />
            <div className="flex min-h-[97px] min-w-0 flex-1 flex-col items-center justify-center text-center leading-[1.6]">
              <Membership className="mb-1.5 size-6 text-white" />
              <p className="font-semibold text-sm text-white">{t('savingTitle')}</p>
              <p className="text-white/40 text-xs">{t('savingDescription')}</p>
            </div>
          </div>
          <Link
            href="/subscription/personal"
            onClick={navigate}
            className={buttonVariants({
              variant: 'solid',
              size: 'lg',
              className: 'w-full max-w-[256px] bg-white font-semibold text-black hover:bg-white/90',
            })}
          >
            {t('buyNow')}
          </Link>
        </div>
      </DialogContent>
    </Dialog>
  );
};
